<template>
  <div class="parse">
    <div class="filter">
      <form class="form-inline" @submit.prevent="handleFileUpload">
        <div class="form-group select-file">
          <input type="file" id="file-upload" @change="handleFileUpload" accept=".fit" multiple class="form-control-file form-control-sm" />
        </div>
      </form>
      <div class="btn-group" role="group">
        <button type="button" class="btn btn-primary btn-sm" @click="toggleView(true)">表格</button>
        <button type="button" class="btn btn-light btn-sm" @click="toggleView(false)" :disabled="!parsedData">图表</button>
        <button type="button" class="btn btn-success btn-sm" @click="downloadExcel" :disabled="!parsedData">下载Excel</button>
      </div>
    </div>

    <!-- 表格 -->
    <div class="parse-result table-box" v-show="showTable">
      <table class="table table-striped table-bordered table-hover table-sm">
        <thead>
          <tr>
            <th scope="col">时间</th>
            <th scope="col">距离(m)</th>
            <th scope="col">心率</th>
            <th scope="col">配速</th>
            <th scope="col">步频</th>
            <th scope="col">步幅(m)</th>
            <th scope="col">功率</th>
            <th scope="col">高度(m)</th>
            <th scope="col">经度</th>
            <th scope="col">纬度</th>
            <th scope="col">触地时间(ms)</th>
            <th scope="col">垂直振幅(cm)</th>
            <th scope="col">垂直振幅比</th>
          </tr>
        </thead>
        <tbody v-if="parsedData">
          <tr v-for="(item, index) in parsedData.records" :key="index">
            <td>{{ convertToBeijingTime(item.timestamp) }}</td>
            <td>{{ item.distance }}</td>
            <td>{{ item.heart_rate }}</td>
            <td>{{ item.enhanced_speed ? convertMpsToPace(item.enhanced_speed) : (item.speed ? convertMpsToPace(item.speed) : '') }}</td>
            <td>{{ item.cadence ? item.cadence *2 : '' }}</td>
            <td>{{ item.step_length ? (item.step_length / 1000).toFixed(2) : '' }}</td>
            <td>{{ item.power }}</td>
            <td>{{ item.enhanced_altitude ? item.enhanced_altitude.toFixed(2) : (item.altitude ? (item.altitude).toFixed(2) : '') }}</td>
            <td>{{ item.position_long }}</td>
            <td>{{ item.position_lat }}</td>
            <td>{{ item.stance_time }}</td>
            <td>{{ item.vertical_oscillation ? (item.vertical_oscillation / 10).toFixed(2) : '' }}</td>
            <td>{{ item.vertical_ratio ? item.vertical_ratio+'%' : '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 图表 -->
    <div class="parse-result chart-box" v-show="!showTable">
      <div class="chart-section" v-if="chartData.length > 0">
        <div
          class="chart-item"
          v-for="(chart, index) in chartData"
          :key="index"
          :ref="'chart' + index"
          style="width: 100%; height: 300px; margin-bottom: 30px;"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import FitFileParser from "fit-file-parser";
import * as echarts from "echarts";
import * as XLSX from "xlsx";

export default {
  name: "DataParse",
  data() {
    return {
      parsedData: null,
      showTable: true,
      chartData: []
    };
  },
  methods: {
    handleFileUpload(event) {
      const files = event.target.files;
      const validFiles = Array.from(files).filter(file => file.name.endsWith('.fit'));

      if (validFiles.length === 0) {
        alert('请上传 .fit 文件');
        return;
      }

      this.parsedData = null;

      validFiles.forEach(file => {
        this.parseFitFile(file);
      });
    },

    parseFitFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const parser = new FitFileParser();
        parser.parse(e.target.result, (error, data) => {
          if (error) {
            alert(`解析文件 ${file.name} 失败`);
          } else {
            this.parsedData = data;
            this.prepareChartData(data.records);
            this.$nextTick(() => {
              console.log('所有refs:', this.$refs);
              this.renderCharts();
            });
          }
        });
      };
      reader.readAsArrayBuffer(file);
    },

    convertToBeijingTime(timestamp) {
      if (!timestamp) return "未知时间";
      const date = new Date(timestamp);
      date.setUTCHours(date.getUTCHours() + 8);
      return date.toISOString().replace("T", " ").split(".")[0];
    },

    convertMpsToPace(mps) {
      const pace = 60 / (mps * 3.6);
      const minutes = Math.floor(pace);
      const seconds = Math.round((pace - minutes) * 60);
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}/km`;
    },

    prepareChartData(records) {
      const timeList = records.map(r => this.convertToBeijingTime(r.timestamp));

      const fields = {
        heart_rate: '心率',
        power: '功率',
        cadence: '步频',
        // 只留一个配速字段：
        // 'enhanced_speed': '配速(由enhanced_speed)', // 先注释掉
        // 'speed': '配速(speed备用)', // 先注释掉
        enhanced_altitude: '高度',
        altitude: '高度(备用)',
        distance: '距离',
        step_length: '步幅',
        stance_time: '触地时间',
        vertical_oscillation: '垂直振幅',
        vertical_ratio: '垂直振幅比',
      };

      this.chartData = [];

      // 判断是否有 enhanced_speed
      const hasEnhancedSpeed = records.some(r => r.enhanced_speed != null);
      const hasSpeed = records.some(r => r.speed != null);

      if (hasEnhancedSpeed) {
        // 优先用 enhanced_speed
        const values = records.map(r => {
          if (r.enhanced_speed == null) return null;
          const pace = 60 / (r.enhanced_speed * 3.6);
          return parseFloat(pace.toFixed(2));
        });
        this.chartData.push({
          label: '配速(由enhanced_speed)',
          key: 'enhanced_speed',
          timeList,
          values,
        });
      } else if (hasSpeed) {
        // 没有 enhanced_speed 才用 speed
        const values = records.map(r => {
          if (r.speed == null) return null;
          const pace = 60 / (r.speed * 3.6);
          return parseFloat(pace.toFixed(2));
        });
        this.chartData.push({
          label: '配速(speed)',
          key: 'speed',
          timeList,
          values,
        });
      }

      // 处理除配速外的其他字段
      for (const key in fields) {
        const label = fields[key];
        const values = records.map(r => {
          if (r[key] == null) return null;

          if (key === 'cadence') return r[key] * 2;
          if (key === 'step_length') return (r[key] / 1000).toFixed(2);
          if (key === 'vertical_oscillation') return (r[key] / 10).toFixed(2);
          if (key === 'vertical_ratio') return parseFloat(r[key]).toFixed(2);

          return r[key];
        });

        if (values.some(v => v != null)) {
          this.chartData.push({
            label,
            key,
            timeList,
            values,
          });
        }
      }
    },

    toggleView(showTable) {
      this.showTable = showTable;
      if (!showTable) {
        this.$nextTick(() => {
          this.renderCharts();
        });
      }
    },

    renderCharts() {
      this.chartInstances = this.chartInstances || [];

      this.chartData.forEach((chart, index) => {
        let chartDom = this.$refs['chart' + index];
        if (Array.isArray(chartDom)) chartDom = chartDom[0];
        if (!chartDom) {
          console.warn(`chart${index} 容器未找到`);
          return;
        }
        chartDom.innerHTML = '';

        if (this.chartInstances[index]) {
          this.chartInstances[index].dispose();
        }

        const myChart = echarts.init(chartDom);
        this.chartInstances[index] = myChart;

        myChart.setOption({
          title: { text: chart.label },
          tooltip: { trigger: 'axis' },
          xAxis: {
            type: 'category',
            data: chart.timeList,
            axisLabel: { rotate: 45, fontSize: 10 }
          },
          yAxis: { type: 'value' },
          dataZoom: [
            { type: 'inside' },
            { type: 'slider' }
          ],
          series: [{
            name: chart.label,
            type: 'line',
            data: chart.values,
            smooth: true,
            showSymbol: false
          }]
        });

        // 保证容器尺寸正确时刷新图表
        myChart.resize();
      });
    },

    downloadExcel() {
      if (!this.parsedData || !this.parsedData.records) {
        alert('暂无数据可导出');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(
        this.parsedData.records.map(item => ({
          时间: this.convertToBeijingTime(item.timestamp),
          距离: item.distance,
          心率: item.heart_rate,
          配速: item.enhanced_speed
            ? this.convertMpsToPace(item.enhanced_speed)
            : (item.speed ? this.convertMpsToPace(item.speed) : ''),
          步频: item.cadence ? item.cadence * 2 : '',
          步幅: item.step_length ? (item.step_length / 1000).toFixed(2) : '',
          功率: item.power,
          高度: item.enhanced_altitude
            ? item.enhanced_altitude.toFixed(2)
            : (item.altitude ? item.altitude.toFixed(2) : ''),
          经度: item.position_long,
          纬度: item.position_lat,
          触地时间: item.stance_time,
          垂直振幅: item.vertical_oscillation
            ? (item.vertical_oscillation / 10).toFixed(2)
            : '',
          垂直振幅比: item.vertical_ratio ? item.vertical_ratio + '%' : '',
        }))
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "运动数据");

      XLSX.writeFile(workbook, "fit数据导出.xlsx");
    }

  }
};
</script>

<style scoped lang="scss">
.parse {padding:20px 0 0; width:100%;}
.filter {width:100%; max-width:$main-width; margin:auto; padding-bottom:20px; display:flex; justify-content: space-between;}
.form-group {margin-right:20px;}
.parse-result {width:100%;}
.table-box {height:calc(100vh - 150px); position:relative; overflow-y: scroll;}
.chart-box {height: auto; overflow: visible; }
.echart-container {width: 100%; height: 100%;}
.chart-section {width: 100%;}
.chart-item {
  width: 100%;
  height: 300px;
  margin-bottom: 30px;
}

@media screen and (max-width: 768px) {
  .parse {padding:10px 0 0;}
  .select-file {margin:0 0 10px;}
  .select-text {display:none;}
  .select-filter {margin:0 10px 0 8px;}
  .filter {padding-bottom:10px;}
}
</style>
